/**
 * Centralized API Configuration
 * Single source of truth for all API-related configuration
 */

import { ApiClient } from '../api/ApiClient';
import { log } from '../utils/errors/logger';
import AuthService from '../services/AuthService';
import { getApiBasePath } from './env';

// Base API URL configuration
export const getApiBaseUrl = () => {
  // In production, use relative /api path (nginx will handle routing)
  // In development, use Vite proxy (empty string = relative paths that get proxied)
  const envUrl = import.meta.env.VITE_API_URL;

  if (envUrl) {
    return envUrl;
  }

  // Auto-detect based on environment
  if (import.meta.env.PROD) {
    // Production: Use current protocol and host to ensure HTTPS is preserved
    return `${window.location.protocol}//${window.location.host}`;
  } else {
    // Development: Use empty string to make relative requests that go through Vite proxy
    // Vite proxies /api -> http://localhost:8080/api (avoids CORS issues)
    return '';
  }
};

// Get API URL for fetch calls (includes /api suffix)
export const getApiFetchUrl = () => {
  return getApiBasePath();
};

// Cookie-based auth: No need for token extraction or refresh functions
// Tokens are in HttpOnly cookies, managed by the backend

// Track retried requests to prevent infinite loops
const retriedRequests = new WeakMap<Request, boolean>();

// Circuit breaker for token refresh - prevent infinite refresh loops
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const BACKOFF_MS = 1000; // 1 second base for exponential backoff

// Create configured API client instance for cookie-based authentication
export const createApiClient = () => {
  const apiInstance = new ApiClient({
    baseUrl: getApiBaseUrl(),

    // Cookie-based auth: Browser automatically sends cookies with credentials: 'include'
    // No need for securityWorker or Authorization headers

    // Custom fetch interceptor to handle 401 responses with automatic token refresh
    customFetch: async (url, requestOptions) => {
      // Proactive token refresh - check if token is expiring soon
      // Skip for refresh endpoint itself to avoid infinite loops
      const isRefreshEndpoint = typeof url === 'string' && url.includes('/auth/refresh');
      if (!isRefreshEndpoint && AuthService.shouldRefreshToken()) {
        log.info('Token expiring soon, proactively refreshing before request', { url });

        try {
          const refreshSuccess = await AuthService.refreshToken();

          if (refreshSuccess) {
            // Note: recordTokenRefresh is already called internally by AuthService.refreshToken()
            log.info('Proactive token refresh successful');
          } else {
            log.warn('Proactive token refresh failed, continuing with request');
          }
        } catch (error) {
          log.warn('Error during proactive token refresh', error);
          // Continue with original request even if proactive refresh fails
        }
      }

      let response = await fetch(url, requestOptions);

      // Handle 401 Unauthorized - attempt token refresh before giving up
      if (response.status === 401) {
        const isRefreshEndpoint = typeof url === 'string' && url.includes('/auth/refresh');
        const hasBeenRetried = requestOptions ? retriedRequests.get(requestOptions as any) : false;

        // Circuit breaker: Check if max refresh attempts exceeded
        if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          log.error('Max token refresh attempts exceeded', {
            attempts: refreshAttempts,
            url,
            currentPath: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
          });
          refreshAttempts = 0; // Reset for next session

          // Only logout/redirect if NOT already on the login page
          // This prevents infinite loop on login page when making authenticated requests
          const isOnLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');

          if (!isOnLoginPage) {
            log.info('Not on login page, clearing auth state and redirecting');

            // Clear auth state and redirect to login
            try {
              const { default: AuthService } = await import('../services/AuthService');
              await AuthService.logout();
              window.dispatchEvent(new Event('authStateChanged'));
            } catch (error) {
              log.error('Error during logout after max refresh attempts', error);
            }

            // Force navigation to login page
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
          } else {
            log.info('Already on login page, skipping logout/redirect to prevent infinite loop');
          }

          return response;
        }

        // Don't retry if:
        // 1. This is the refresh endpoint itself (avoid infinite loop)
        // 2. This request has already been retried (avoid infinite loop)
        if (isRefreshEndpoint || hasBeenRetried) {
          log.warn('Received 401 (Unauthorized) - cannot retry', {
            isRefreshEndpoint,
            hasBeenRetried,
            url
          });
          // Reset counter on terminal failure
          refreshAttempts = 0;

          // Clear auth state and dispatch event for login redirect
          try {
            const { default: AuthService } = await import('../services/AuthService');
            await AuthService.logout();
            window.dispatchEvent(new Event('authStateChanged'));
          } catch (error) {
            log.error('Error during logout after 401', error);
          }
          return response;
        }

        // Increment attempt counter
        refreshAttempts++;

        // Exponential backoff before retry
        const backoffDelay = BACKOFF_MS * Math.pow(2, refreshAttempts - 1);
        log.info('Attempting token refresh with backoff', {
          attempt: refreshAttempts,
          maxAttempts: MAX_REFRESH_ATTEMPTS,
          backoffMs: backoffDelay,
          url
        });

        await new Promise(resolve => setTimeout(resolve, backoffDelay));

        try {
          // Dynamic import to avoid circular dependencies
          const { default: AuthService } = await import('../services/AuthService');
          const refreshSuccess = await AuthService.refreshToken();

          if (refreshSuccess) {
            log.info('Token refresh successful, retrying original request', {
              attempt: refreshAttempts,
              url
            });

            // Reset counter on successful refresh
            refreshAttempts = 0;

            // Note: recordTokenRefresh is already called internally by AuthService.refreshToken()

            // Mark this request as retried to prevent infinite loops
            if (requestOptions) {
              retriedRequests.set(requestOptions as any, true);
            }

            // Retry the original request with refreshed token (cookies automatically included)
            response = await fetch(url, requestOptions);

            if (response.status === 401) {
              log.warn('Still received 401 after refresh, clearing auth state', { url });
              await AuthService.logout();
              window.dispatchEvent(new Event('authStateChanged'));
            } else {
              log.info('Original request succeeded after token refresh', {
                url,
                status: response.status
              });
            }
          } else {
            // Refresh failed - don't redirect immediately, let retry logic continue
            log.warn('Token refresh failed', {
              attempt: refreshAttempts,
              maxAttempts: MAX_REFRESH_ATTEMPTS
            });

            // If this was the last attempt, redirect to login (unless already on login page)
            if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
              log.error('Max refresh attempts reached after failed refresh');
              refreshAttempts = 0;

              const isOnLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
              if (!isOnLoginPage) {
                await AuthService.logout();
                window.dispatchEvent(new Event('authStateChanged'));
                if (typeof window !== 'undefined') {
                  window.location.href = '/login';
                }
              } else {
                log.info('Already on login page, skipping logout/redirect');
              }
            }
          }
        } catch (error) {
          log.error('Error during automatic token refresh flow', {
            error,
            url,
            attempt: refreshAttempts
          });
          // On error, don't immediately logout - let circuit breaker handle it
          // If max attempts reached, force logout (unless already on login page)
          if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
            refreshAttempts = 0;

            const isOnLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
            if (!isOnLoginPage) {
              try {
                const { default: AuthService } = await import('../services/AuthService');
                await AuthService.logout();
                window.dispatchEvent(new Event('authStateChanged'));
                if (typeof window !== 'undefined') {
                  window.location.href = '/login';
                }
              } catch (logoutError) {
                log.error('Error during logout after refresh failure', logoutError);
              }
            } else {
              log.info('Already on login page, skipping logout/redirect after refresh error');
            }
          }
        }
      } else {
        // Successful response - reset refresh attempts counter
        refreshAttempts = 0;
      }

      return response;
    }
  });

  return apiInstance;
};

// Export singleton instance for global use
export const apiClient = createApiClient();
